import type { AllPages } from '@brush/api-client';
import type { PointsScope } from '@brush/ui';

/**
 * The scope filters' candidate list (known gap 35): the tournaments a leaderboard's or a rating's scope could
 * score, read from the room's list page by page and then marked here. Leaderboards and the rating builder
 * render the same list (`LeaderboardScopeFilters`).
 *
 * Type imports only, so `node --test` runs this file as it is.
 */

/** The fields of a room-list row the tick list draws. */
export interface CandidateRow {
  id: string;
  internalId: string;
  name: string;
  date: string;
  buyIn: number;
  entries: number;
  tags: string[];
}

/** What the candidate read sends besides the page: the scope's bounds, and its required tags as `anyTags`. */
export interface CandidateParams {
  from?: string;
  to?: string;
  anyTags?: string[];
}

/** Every page read, or as many as could be (`fetchAllPages` in `@brush/api-client`). */
export type CandidateResult<R> = AllPages<R>;

/**
 * The read for a scope: its bounds when it has them (a rating's may be open, which is the whole room) and its
 * required tags, which the server matches any-of. **Nothing about exclusions or ids**: an unticked tournament
 * must come back to be drawn struck through, and an excluded tag is dropped here (`candidateRows`), which
 * keeps an exclude chip instant. Ticking a row therefore changes nothing this sends.
 */
export function candidateParams(scope: PointsScope): CandidateParams {
  const params: CandidateParams = {};
  if (scope.startDate) params.from = scope.startDate;
  if (scope.endDate) params.to = scope.endDate;
  const anyTags = scope.includeTags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
  if (anyTags.length > 0) params.anyTags = anyTags;
  return params;
}

/**
 * Two tags are one when the server's `excludeTags` and `tags` would treat them as one
 * (`TournamentService.Matching`): it trims what it is asked for, stores tags trimmed, and compares under a
 * case-insensitive collation. So: trimmed, then lower-cased. The one difference left is an expansion (`ß`
 * equals `SS` in SQL Server, not here), which the chips cannot produce — they offer the tags the room carries.
 */
export function sameTag(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * The rows the tick list shows, in date order, each saying whether it is unticked (drawn struck through).
 *
 * The scope's tag rules are applied here as well as on the server, against the scope **as it is now**, so a
 * narrowing edit (an excluded tag, a required tag dropped, a date pulled in) shows at once while the read for a
 * wider set is still on its way. A tournament an excluded tag vetoes is **not listed**: struck through means
 * unticked, an exception the operator made and can undo, which a tag rule is not. Required tags are §3.2's —
 * at least one — never the tournaments list's all-of.
 *
 * `withinDates` is the caller's, so the date rule keeps its one implementation (`withinScopeDates` in
 * `@brush/ui`, where an open bound is handled once).
 */
export function candidateRows<R extends CandidateRow>(
  rows: readonly R[],
  scope: PointsScope,
  withinDates: (date: string) => boolean,
): { tournament: R; unticked: boolean }[] {
  const has = (tags: string[], tag: string) => tags.some((t) => sameTag(t, tag));
  // Blank entries name no tag, and the server drops them before matching (`CleanTags`).
  const required = scope.includeTags.filter((tag) => tag.trim().length > 0);
  const excluded = scope.excludeTags.filter((tag) => tag.trim().length > 0);
  const unticked = new Set(scope.excludeEventIds);

  return rows
    .filter((t) => withinDates(t.date))
    .filter((t) => !excluded.some((tag) => has(t.tags, tag)))
    .filter((t) => required.length === 0 || required.some((tag) => has(t.tags, tag)))
    .sort((a, b) => a.date.localeCompare(b.date) || a.internalId.localeCompare(b.internalId))
    .map((tournament) => ({ tournament, unticked: unticked.has(tournament.id) }));
}

/** What the tick list's header says (`LeaderboardScopeFilters`). */
export type CandidateHeader = { kind: 'loading' } | { kind: 'failed' } | { kind: 'count'; chosen: number; of: number };

/**
 * A count only once there is a list (#224). With no answer and nothing on its way the read failed, and the list
 * below says so with Try again; "0 of 0 selected" above it was a count about a list that never loaded. An empty
 * answer is a list, so 0 of 0 stays true there.
 */
export function candidateHeader(list: { loading: boolean; result: unknown }, chosen: number, of: number): CandidateHeader {
  if (list.loading) return { kind: 'loading' };
  return list.result === undefined ? { kind: 'failed' } : { kind: 'count', chosen, of };
}

/** Which screen the scope filters are on: the leaderboard dialog, or the rating builder that reuses them. */
export type ScopeSubject = 'leaderboard' | 'rating';

/**
 * What to widen when no tournament matches (#545). The list is shared, and the two screens bound it differently: a
 * leaderboard by its own start and end dates, a rating by the From and To fields in its Which events section.
 */
export function emptyCandidatesHint(subject: ScopeSubject): string {
  return subject === 'rating'
    ? 'Widen the rating’s From and To dates, or clear a tag filter.'
    : 'Widen the leaderboard’s start and end dates, or clear a tag filter.';
}
